import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import * as SyntheticUsersService from '../services/SyntheticUsersService';
import {SyntheticUsersFilter} from '../types';
import {testingApiAvailable} from '../../testing-api-integration/config';
import {
    notifyError,
    notifySuccess,
} from '../../../components/PlatformNotification';

// FNP-861 shipped this module query-only. FNP-969 adds exactly ONE mutation hook:
// retire. Still no create and no update — and no delete hook can exist here,
// because the API has no delete mutation to call.
export const syntheticUsersKeys = {
    all: ['synthetic-users'] as const,
    lists: () => [...syntheticUsersKeys.all, 'list'] as const,
    list: (filters: SyntheticUsersFilter) => [...syntheticUsersKeys.lists(), {filters}] as const,
    details: () => [...syntheticUsersKeys.all, 'detail'] as const,
    detail: (id: string) => [...syntheticUsersKeys.details(), id] as const,
};

const PAGE_SIZE = 50;

export const useSyntheticUsersInfiniteQuery = (filters: SyntheticUsersFilter = {}) => {
    return useInfiniteQuery({
                                queryKey: syntheticUsersKeys.list(filters),
                                queryFn: ({pageParam}: { pageParam: string | null }) =>
                                    SyntheticUsersService.fetchRecords(filters, PAGE_SIZE, pageParam),
                                // Non-key FilterExpressions can return a near-empty page with a
                                // non-null nextToken — page on nextToken alone, never stop on empty.
                                getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
                                initialPageParam: null as string | null,
                                enabled: testingApiAvailable,
                            });
};

export const useSyntheticUserQuery = (id: string) => {
    return useQuery({
                        queryKey: syntheticUsersKeys.detail(id),
                        queryFn: () => SyntheticUsersService.fetch(id),
                        enabled: testingApiAvailable && !!id,
                    });
};

// Retire = tombstone. The success message says "retired", never "deleted": the row
// is still there and the operator must not be told otherwise, or they will go
// looking for a vanished row that never vanished.
//
// Both the list and the detail entry are invalidated — the row does not leave the
// list, it CHANGES (to DELETED / TOMBSTONED), so a stale list would keep showing it
// as ACTIVE and invite a second retire attempt.
export const useRetireSyntheticUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: SyntheticUsersService.retire,
                           onSuccess: (_, id) => {
                               notifySuccess('Synthetic user retired — the row is kept as a tombstone');
                               queryClient.invalidateQueries({queryKey: syntheticUsersKeys.lists()});
                               queryClient.invalidateQueries({queryKey: syntheticUsersKeys.detail(id)});
                           },
                           onError: notifyError,
                       });
};
